import React from "react";
import { StoreSearchFilters } from "tsi-common-react/src/apps/retail/models";
import { storeFilterSelector } from "tsi-common-react/src/apps/retail/selectors";
import { LocationInput } from "tsi-common-react/src/components/LocationInput/index";
import { FORM_FIELD_CLASS_NAME } from "tsi-common-react/src/forms/classNames";
import { ILocation } from "tsi-common-react/src/models/location";
import { Meters } from "tsi-common-react/src/models/nominals";
import { useAppSelector } from "tsi-common-react/src/store/reducers";
import {
    DISTANCE_OPTIONS,
    DistanceUnit,
    displayUnitToMeters,
    getDistanceUnit,
    metersToDisplayUnit,
} from "tsi-common-react/src/utils/distance";
import { c, t } from "ttag";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { LocatorFilterPopover } from "../LocatorFilters_Popover";
import { LOCATION_DIALOG_TITLE_ID } from "../constants";
import { FilterChangeHandler } from "../models";

const LOCATION_INPUT_ID = "location-autocomplete";

const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
};

const getDistanceFromEvent = (
    event: React.FormEvent<HTMLSelectElement>,
): Meters => {
    const displayValue = parseInt(event.currentTarget.value, 10);
    return displayUnitToMeters(displayValue);
};

const getLabelText = (currentLocation: ILocation | null): string => {
    return (
        currentLocation?.formatted_address_long ||
        currentLocation?.formatted_address ||
        t`you`
    );
};

export const LocationFilter = (props: {
    filterValues: StoreSearchFilters;
    currentLocation: ILocation | null;
    onChange: FilterChangeHandler;
}): React.ReactNode => {
    const storeFilters = useAppSelector(storeFilterSelector);
    const [distanceValue, setDistanceValue] = React.useState<Meters>(
        props.filterValues.distance,
    );
    const filterElemRef = React.useRef<{
        onRequestClose: () => void;
    }>(null);

    const unit = getDistanceUnit();
    const distanceOptions = DISTANCE_OPTIONS[unit];
    const labelPattern =
        unit === DistanceUnit.KILOMETERS
            ? c("LocationFilter").t`%s km`
            : c("LocationFilter").t`%s miles`;

    // Sync distance value when it changes from external source
    React.useEffect(() => {
        setDistanceValue(storeFilters.distance);
    }, [storeFilters.distance]);

    const handleDistanceChange = (
        event: React.FormEvent<HTMLSelectElement>,
    ) => {
        const newDistance = getDistanceFromEvent(event);
        setDistanceValue(newDistance);
        props.onChange({
            distance: newDistance,
        });
    };

    const handleAfterLocationChange = () => {
        if (filterElemRef.current) {
            filterElemRef.current.onRequestClose();
        }
    };

    const labelText = getLabelText(props.currentLocation);

    // Convert meters to display unit for the select value
    const displayValue = Math.round(metersToDisplayUnit(distanceValue));

    return (
        <LocatorFilterPopover
            ref={filterElemRef}
            id="location"
            labelText={labelText}
            dialogLabelledById={LOCATION_DIALOG_TITLE_ID}
        >
            <form
                className="filters__dropdown filters__dropdown--location"
                onSubmit={handleSubmit}
            >
                <fieldset>
                    <legend
                        className="filters__instruct"
                        id={LOCATION_DIALOG_TITLE_ID}
                    >
                        {t`Change your location to find retailers in that area.`}
                    </legend>
                    <div
                        className={concatClassNames([
                            FORM_FIELD_CLASS_NAME,
                            "filters__inputs",
                        ])}
                    >
                        <div className="form__input form__input--zip">
                            <label
                                htmlFor={LOCATION_INPUT_ID}
                                className="ada-screenreader-only"
                            >
                                {t`Location`}
                            </label>
                            <LocationInput
                                inputProps={{
                                    id: LOCATION_INPUT_ID,
                                    name: "bh-sl-address",
                                    placeholder: t`Enter address, city, state or zip`,
                                    title: t`Enter address, city, state or zip`,
                                }}
                                inputFormat="long"
                                onAfterChange={handleAfterLocationChange}
                            />
                        </div>
                    </div>
                    <div
                        className={concatClassNames([
                            FORM_FIELD_CLASS_NAME,
                            "filters__inputs",
                        ])}
                    >
                        <div className="form__input form__input--distance">
                            <select
                                className="form__select"
                                name="bh-sl-maxdistance"
                                aria-label={t`Max Distance`}
                                onChange={handleDistanceChange}
                                value={displayValue}
                            >
                                {distanceOptions.map((opt) => {
                                    return (
                                        <option key={opt} value={opt}>
                                            {interpolate(labelPattern, [opt])}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                    </div>
                    <div className="filters__actions sl-search__actions al-location--submit-zip">
                        <input
                            className="button filters__button"
                            type="submit"
                            aria-label={t`Search`}
                        />
                    </div>
                </fieldset>
            </form>
        </LocatorFilterPopover>
    );
};
